import React, { Component, ErrorInfo, ReactNode } from 'react';
import { KeycloakService } from '../../services/keycloakService';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  isAuthError: boolean;
}

/**
 * Error boundary specifically designed to handle authentication-related errors
 * Provides graceful fallback UI and automatic recovery mechanisms
 */
export class AuthErrorBoundary extends Component<Props, State> {
  private retryCount = 0;
  private maxRetries = 3;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      isAuthError: false
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Check if this is an authentication-related error
    const isAuthError = AuthErrorBoundary.isAuthenticationError(error);
    
    return {
      hasError: true,
      error,
      isAuthError
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('AuthErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      errorInfo
    });

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo);

    // Handle authentication errors automatically
    if (this.state.isAuthError) {
      this.handleAuthError(error);
    }
  }

  /**
   * Determines if an error is authentication-related
   */
  private static isAuthenticationError(error: Error): boolean {
    const authErrorPatterns = [
      /unauthorized/i,
      /401/,
      /token.*expired/i,
      /token.*invalid/i,
      /authentication.*failed/i,
      /keycloak/i,
      /refresh.*token/i,
      /access.*denied/i
    ];

    const errorMessage = error.message || error.toString();
    const errorStack = error.stack || '';
    
    return authErrorPatterns.some(pattern => 
      pattern.test(errorMessage) || pattern.test(errorStack)
    );
  }

  /**
   * Handles authentication errors with automatic recovery attempts
   */
  private handleAuthError = async (error: Error) => {
    console.warn('Handling authentication error:', error.message);

    if (this.retryCount < this.maxRetries) {
      this.retryCount++;
      console.log(`Attempting authentication recovery (attempt ${this.retryCount}/${this.maxRetries})`);

      try {
        // Try to refresh the token first
        const tokenRefreshed = await KeycloakService.updateToken(60);
        
        if (tokenRefreshed) {
          console.log('Token refreshed successfully, recovering from auth error');
          this.handleRecovery();
          return;
        }

        // If token refresh failed, try to reinitialize Keycloak
        const reinitialized = await KeycloakService.init();
        
        if (reinitialized) {
          console.log('Keycloak reinitialized successfully, recovering from auth error');
          this.handleRecovery();
          return;
        }

        // If all recovery attempts failed, redirect to login
        console.warn('All recovery attempts failed, redirecting to login');
        this.redirectToLogin();
        
      } catch (recoveryError) {
        console.error('Error during authentication recovery:', recoveryError);
        
        if (this.retryCount >= this.maxRetries) {
          console.error('Max recovery attempts reached, redirecting to login');
          this.redirectToLogin();
        } else {
          // Wait before next retry
          setTimeout(() => this.handleAuthError(error), 2000 * this.retryCount);
        }
      }
    } else {
      console.error('Max authentication recovery attempts reached');
      this.redirectToLogin();
    }
  };

  /**
   * Recovers from error state after successful authentication recovery
   */
  private handleRecovery = () => {
    this.retryCount = 0;
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      isAuthError: false
    });
  };

  /**
   * Redirects user to login page
   */
  private redirectToLogin = () => {
    const currentLocation = window.location.pathname + window.location.search;
    sessionStorage.setItem('redirect_after_login', currentLocation);
    KeycloakService.login(currentLocation);
  };

  /**
   * Manual retry function for user-initiated recovery
   */
  private handleRetry = () => {
    if (this.state.isAuthError) {
      this.retryCount = 0; // Reset retry count for manual retry
      this.handleAuthError(this.state.error!);
    } else {
      // For non-auth errors, just reset the error boundary
      this.handleRecovery();
    }
  };

  /**
   * Reload the entire page as last resort
   */
  private handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="auth-error-boundary">
          <div className="error-container">
            <div className="error-icon">⚠️</div>
            <h2>
              {this.state.isAuthError 
                ? 'Problem z uwierzytelnianiem' 
                : 'Wystąpił nieoczekiwany błąd'
              }
            </h2>
            <p className="error-message">
              {this.state.isAuthError
                ? 'Wystąpił problem z Twoją sesją. Próbujemy automatycznie rozwiązać problem...'
                : 'Coś poszło nie tak. Spróbuj odświeżyć stronę lub skontaktuj się z administratorem.'
              }
            </p>
            
            {this.state.error && (
              <details className="error-details">
                <summary>Szczegóły błędu</summary>
                <pre>{this.state.error.message}</pre>
                {this.state.errorInfo && (
                  <pre>{this.state.errorInfo.componentStack}</pre>
                )}
              </details>
            )}

            <div className="error-actions">
              <button 
                onClick={this.handleRetry}
                className="retry-button"
                disabled={this.retryCount >= this.maxRetries}
              >
                {this.state.isAuthError ? 'Spróbuj ponownie' : 'Ponów'}
              </button>
              
              <button 
                onClick={this.handleReload}
                className="reload-button"
              >
                Odśwież stronę
              </button>

              {this.state.isAuthError && (
                <button 
                  onClick={this.redirectToLogin}
                  className="login-button"
                >
                  Przejdź do logowania
                </button>
              )}
            </div>

            {this.retryCount > 0 && (
              <p className="retry-info">
                Próba {this.retryCount} z {this.maxRetries}
              </p>
            )}
          </div>

          <style>{`
            .auth-error-boundary {
              display: flex;
              justify-content: center;
              align-items: center;
              min-height: 400px;
              padding: 20px;
              background-color: #f8f9fa;
            }

            .error-container {
              max-width: 500px;
              text-align: center;
              background: white;
              padding: 40px;
              border-radius: 8px;
              box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
            }

            .error-icon {
              font-size: 48px;
              margin-bottom: 20px;
            }

            .error-message {
              color: #666;
              margin-bottom: 30px;
              line-height: 1.5;
            }

            .error-details {
              text-align: left;
              margin: 20px 0;
              padding: 15px;
              background-color: #f8f9fa;
              border-radius: 4px;
            }

            .error-details pre {
              white-space: pre-wrap;
              font-size: 12px;
              color: #666;
              margin: 10px 0;
            }

            .error-actions {
              display: flex;
              gap: 10px;
              justify-content: center;
              flex-wrap: wrap;
            }

            .retry-button, .reload-button, .login-button {
              padding: 10px 20px;
              border: none;
              border-radius: 4px;
              cursor: pointer;
              font-size: 14px;
              transition: background-color 0.2s;
            }

            .retry-button {
              background-color: #007bff;
              color: white;
            }

            .retry-button:hover:not(:disabled) {
              background-color: #0056b3;
            }

            .retry-button:disabled {
              background-color: #ccc;
              cursor: not-allowed;
            }

            .reload-button {
              background-color: #6c757d;
              color: white;
            }

            .reload-button:hover {
              background-color: #545b62;
            }

            .login-button {
              background-color: #28a745;
              color: white;
            }

            .login-button:hover {
              background-color: #1e7e34;
            }

            .retry-info {
              margin-top: 15px;
              font-size: 12px;
              color: #666;
            }
          `}</style>
        </div>
      );
    }

    return this.props.children;
  }
}